
import { Component, Vue, Prop } from "vue-property-decorator";
import { City } from "@/models/City";
import { DateTime, Interval } from "luxon";
import ProgressDialog from "@/components/Base/ProgressDialog.vue";
import { Game } from "@/models/Game";
import { cloneDeep, toArray } from "lodash";
import { Validations } from "vuelidate-property-decorators";
import { required, minLength, maxLength } from "vuelidate/lib/validators";

@Component({
  components: { ProgressDialog },
})
class CloneGame extends Vue {
  @Prop({ default: null }) readonly cloneDataObject!: Game;
  adminName = "";
  selectedCity = "";
  cloneData = {} as Game;
  errorMessage: string[] = [];
  warningMessage: string[] = [];
  startDate = DateTime.now()
    .plus({ days: 1 })
    .toString();
  min = new Date();
  max = DateTime.now()
    .plus({ days: 720 })
    .toString();
  endDate = DateTime.now()
    .plus({ days: 1 })
    .toString();
  endTime = "15:00";
  startTime = "08:00";

  get getCities(): string[] {
    const results: string[] = this.$store.state.cityStore.city.map(
      (t: City) => t.name.en
    );
    return results.sort();
  }
  get getCityData(): City {
    return this.$store.state.cityStore.clonedCity;
  }

  get getCityDataFromSlug(): City {
    return this.$store.state.cityStore.city.find(
      (x: City) => x.name.en === this.selectedCity
    );
  }

  get getTimeZone(): string {
    if (this.getCityData.tZone === undefined) {
      return "Invalid ";
    } else {
      return (
        this.getCityData.tZone!.timeZoneId +
        ", " +
        this.getCityData.tZone!.timeZoneName +
        " (" +
        DateTime.fromISO(this.cloneData.start_dt, {
          zone: this.getCityData.tZone!.timeZoneId,
        }).offset /
          60 +
        ")"
      );
    }
  }

  created(): void {
    this.cloneData = cloneDeep(this.cloneDataObject);
    this.$store.dispatch("cityStore/getClonedCity", this.cloneData.city);
    this.selectedCity = this.getCityData.name.en;
    this.startDate = DateTime.now()
      .plus({ days: 1 })
      .setZone(this.getCityData.tZone!.timeZoneId)
      .toString();
    this.endDate = DateTime.now()
      .plus({ days: 1 })
      .setZone(this.getCityData.tZone!.timeZoneId)
      .toString();
    this.startTime = DateTime.fromISO(this.cloneData.start_dt.toString())
      .setZone(this.getCityData.tZone!.timeZoneId)
      .toFormat("HH:mm");

    this.endTime = DateTime.fromISO(this.cloneData.end_dt.toString())
      .setZone(this.getCityData.tZone!.timeZoneId)
      .toFormat("HH:mm");
    this.checkWarningDate();
  }

  cancel(): void {
    this.$emit("closeDialog");
    this.$v.$touch();
  }
  saveGame(): void {
    this.$v.$touch();
    this.errorMessage = [];
    if (this.adminName.length < 3) {
      this.errorMessage.push("Please enter in an admin name");
    }
    if (this.checkDate()) {
      this.errorMessage.push(
        "Please make sure the end date is in front of the start date"
      );
    }
    if (this.getCityData.tZone === undefined) {
      this.errorMessage.push("Selected City does not hava a valid Timezone");
    }
    if (this.errorMessage.length == 0) {
      this.$emit("closeDialog");
      this.$bvModal.show("team-modal-spinner");
      this.$store.dispatch(
        "mapPoiStore/getMapPoiForGameObject",
        this.cloneData.key
      );

      var clonedCity = this.cloneData.city;
      this.cloneData.city = this.getCityData.slug;
      this.cloneData.start_dt = this.getUTCTime(
        this.startDate.toString(),
        this.startTime
      );
      this.cloneData.end_dt = this.getUTCTime(
        this.endDate.toString(),
        this.endTime
      );
      this.cloneData.admin_name = this.adminName;
      this.cloneData.notes = "";

      var data = {
        clonedGame: this.cloneData,
        clonedCity: clonedCity,
        cityData: this.getCityData,
        mapPoi: this.$store.state.mapPoiStore.mapPoiObject,
        quest: "",
      };
      if (data.clonedGame.gt_uid === "quest") {
        data.quest = this.$store.state.questStore.questObject;
      }
      this.$store.dispatch("gameStore/cloneGame", data).then((result) => {
        this.$bvModal.hide("team-modal-spinner");
      });
      this.$emit("handlelock");
      this.$emit("closeDialogLanguage");
    }
  }

  getUTCTime(date: string, time: string): string {
    var time2 = DateTime.fromISO(time);
    if (this.getCityData.tZone !== undefined) {
      return DateTime.fromISO(date.toString(), {
        zone: this.getCityData.tZone!.timeZoneId,
      })
        .set({
          hour: time2.hour,
          minute: time2.minute,
          second: 0,
          millisecond: 0,
        })
        .toISO();
    } else {
      return DateTime.fromISO(date.toString())
        .set({
          hour: time2.hour,
          minute: time2.minute,
          second: 0,
          millisecond: 0,
        })
        .toISO();
    }
  }

  get getStartDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.startDate.toString()).toFormat("dd/MM/yyyy") +
      " @ " +
      this.startTime
    );
  }
  get getEndDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.endDate.toString()).toFormat("dd/MM/yyyy") +
      " @ " +
      this.endTime
    );
  }

  checkWarningDate(): void {
    this.warningMessage = [];
    var startDate = DateTime.fromISO(
      this.getUTCTime(this.startDate, this.startTime)
    );
    var endDate = DateTime.fromISO(this.getUTCTime(this.endDate, this.endTime));
    var todayDate = new Date();
    const diff = Interval.fromDateTimes(todayDate, startDate);
    if (startDate > endDate) {
      this.warningMessage.push("The end date must come after the start date");
    }
    if (diff.length("hours") < 48) {
      this.warningMessage.push(
        "Date is less than 48 hours away. Please be careful as this data might be purged"
      );
    }
  }

  onValuedChanged(name: string): void {
    this.selectedCity = name;
    this.$store.dispatch(
      "cityStore/getClonedCity",
      this.getCityDataFromSlug.slug
    );
  }

  checkDate(): boolean {
    if (
      DateTime.fromISO(this.getUTCTime(this.startDate, this.startTime)) <
      DateTime.fromISO(this.getUTCTime(this.endDate, this.endTime))
    ) {
      return false;
    } else {
      return true;
    }
  }

  @Validations()
  validations = {
    adminName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(160),
    },
  };
}

export default CloneGame;
